import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  getGuidePage,
  pageTypeToGuideTab,
  type GuidePageContent,
  type GuideSection,
  type GuideTabId,
} from '../../page/guide/guideContent';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { Language, MemberType } from '../../shared/enum/EnumRepository';
import { useLanguageStore } from '../../shared/store/languageStore';
import { useSettingStore } from '../../shared/store/settingStore';
import { resolveCurrentPage } from '../../shared/util/currentPage';

const useGuideModalView = () => {
  const { pathname } = useLocation();
  const memberType = useSettingStore((s) => s.setting?.memberType);
  const routeTab = pageTypeToGuideTab(resolveCurrentPage(pathname).page);
  const [language, setLanguage] = useState(() => useLanguageStore.getState().language);
  const [activeTab, setActiveTab] = useState<GuideTabId>(routeTab);
  const page = getGuidePage(language, activeTab);
  const showGroupGuides = memberType === MemberType.GROUP;
  const sections = page.sections.filter(
    (section) => showGroupGuides || !section.groupOnly,
  );
  return {
    language,
    setLanguage,
    routeTab,
    activeTab,
    setActiveTab,
    page,
    sections,
    routeLabel: getGuidePage(language, routeTab).tabLabel,
    overallLabel: getGuidePage(language, 'overall').tabLabel,
  };
};

interface GuideModalPaneProps {
  onClose: () => void;
  closeIcon: string;
  closeHoverOpacity?: boolean;
  isMobile?: boolean;
}

/** 제목, 경로/Overall 전환, KOR/ENG, 본문. */
export const GuideModalPane: React.FC<GuideModalPaneProps> = ({
  onClose,
  closeIcon,
  closeHoverOpacity = false,
  isMobile = false,
}) => {
  const view = useGuideModalView();
  const showScopeSwitch = view.routeTab !== 'overall';

  return (
    <>
      <HeaderRow $isMobile={isMobile}>
        <TitleBlock $isMobile={isMobile}>
          <TitleLine>
            <Title as={isMobile ? 'h2' : 'div'} $isMobile={isMobile}>
              {view.page.tabLabel}
            </Title>
            {showScopeSwitch ? (
              <ScopeButton
                type="button"
                onClick={() =>
                  view.setActiveTab(
                    view.activeTab === 'overall' ? view.routeTab : 'overall',
                  )
                }
              >
                {`/ ${view.activeTab === 'overall' ? view.routeLabel : view.overallLabel}`}
              </ScopeButton>
            ) : null}
          </TitleLine>
          <UnderTitle>
            <LanguageSelector>
              <LanguageOption
                type="button"
                $isActive={view.language === Language.KR}
                onClick={() => view.setLanguage(Language.KR)}
              >
                KOR
              </LanguageOption>
              <LanguageDivider>/</LanguageDivider>
              <LanguageOption
                type="button"
                $isActive={view.language === Language.EN}
                onClick={() => view.setLanguage(Language.EN)}
              >
                ENG
              </LanguageOption>
            </LanguageSelector>
          </UnderTitle>
        </TitleBlock>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          hoverOpacity={closeHoverOpacity}
          backgroundImg={closeIcon}
          onClick={onClose}
        />
      </HeaderRow>
      <GuideModalContent
        key={`${view.activeTab}-${view.language}`}
        language={view.language}
        page={view.page}
        sections={view.sections}
      />
    </>
  );
};

interface GuideModalContentProps {
  language: Language;
  page: GuidePageContent;
  sections: GuideSection[];
}

/** 현재 경로에 해당하는 가이드 탭 본문만 보여 준다. */
const GuideModalContent: React.FC<GuideModalContentProps> = ({
  language,
  page,
  sections,
}) => {

  return (
    <Content>
      <Intro>
        {page.intro.map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 ? <br /> : null}
            {line}
          </React.Fragment>
        ))}
      </Intro>
      {page.introImages?.length ? (
        <IntroFigureRow>
          {page.introImages.map((src, i) => (
            <SectionFigure key={i}>
              <SectionImage
                src={src}
                alt={
                  language === Language.KR
                    ? `${page.tabLabel} 소개 참고 이미지 ${i + 1}`
                    : `${page.tabLabel} intro reference ${i + 1}`
                }
                loading="lazy"
                decoding="async"
              />
            </SectionFigure>
          ))}
        </IntroFigureRow>
      ) : null}
      {sections.map((section) => (
        <SectionBlock key={section.title}>
          <SmallTitle>{section.title}</SmallTitle>
          {section.imageSrc ? (
            <SectionFigure>
              <SectionImage
                src={section.imageSrc}
                alt={
                  language === Language.KR
                    ? `${section.title} 참고 이미지`
                    : `${section.title} reference image`
                }
                loading="lazy"
                decoding="async"
              />
            </SectionFigure>
          ) : null}
          <BulletList>
            {section.items.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </BulletList>
        </SectionBlock>
      ))}
    </Content>
  );
};

const HeaderRow = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  margin-top: ${({ $isMobile }) => ($isMobile ? 'clamp(12px, 2vh, 30px)' : '0')};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? 'clamp(20px, 8vh, 30px)' : '0')};
`;

const TitleBlock = styled.div<{ $isMobile: boolean }>`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '0' : '12px')};
`;

const TitleLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.div<{ $isMobile: boolean }>`
  min-width: 0;
  margin: 0;
  ${({ theme, $isMobile }) =>
    $isMobile ? theme.typography.Body_01_1 : theme.typography.Body_02_1};
  color: ${({ theme, $isMobile }) =>
    $isMobile ? theme.colors.color_Gray_02 : 'inherit'};
`;

const UnderTitle = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 16px;
`;

const ScopeButton = styled.button`
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  -webkit-tap-highlight-color: transparent;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_02};
  }
`;

const LanguageSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LanguageOption = styled.button<{ $isActive: boolean }>`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.color_Gray_04 : theme.colors.color_Gray_05};
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_04};
  }
`;

const LanguageDivider = styled.span`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_05};
  user-select: none;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 12px;
  padding-bottom: 24px;
  margin-bottom: 8px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Intro = styled.p`
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  line-height: 1.45;
`;

const IntroFigureRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 8px;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionFigure = styled.figure`
  margin: 0 0 4px;
`;

const SectionImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 2px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
`;

const SmallTitle = styled.h2`
  margin: 12px 0 4px;
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const BulletList = styled.ul`
  width: 100%;
  margin: 0;
  padding-left: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  line-height: 1.45;
  list-style: disc;

  li {
    margin-bottom: 6px;
  }
`;
